import type { FormRule, FormTrigger, ValidateMessages } from './types';

const DEFAULT_MESSAGES: Required<ValidateMessages> = {
  required: '${label}为必填项',
  pattern: '${label}格式不正确',
  validator: '${label}校验不通过',
};

function formatMessage(template: string, label: string): string {
  return template.replace(/\$\{label\}/g, label || '该字段');
}

function getMessageFromRule(
  rule: FormRule,
  value: any,
  options: { type: keyof ValidateMessages; label: string; validateMessages?: ValidateMessages },
): string {
  if (rule.message) {
    if (typeof rule.message === 'function') {
      return rule.message(value, rule);
    }
    return rule.message;
  }
  const messages = { ...DEFAULT_MESSAGES, ...options.validateMessages };
  return formatMessage(messages[options.type], options.label);
}

function getRuleTrigger(rule: FormRule): FormTrigger[] {
  if (!rule.trigger) return [];
  if (Array.isArray(rule.trigger)) return rule.trigger;
  return [rule.trigger];
}

export interface FilterRulesOptions {
  trigger?: FormTrigger;
  itemTrigger?: FormTrigger | FormTrigger[];
  formTrigger: FormTrigger | FormTrigger[];
}

export function filterRulesByTrigger(rules: FormRule[], options: FilterRulesOptions): FormRule[] {
  const { trigger, itemTrigger, formTrigger } = options;
  if (!trigger) return rules;

  return rules.filter((rule) => {
    const ruleTriggers = getRuleTrigger(rule);
    if (ruleTriggers.length > 0) {
      return ruleTriggers.includes(trigger);
    }
    const effectiveTrigger = itemTrigger || formTrigger;
    const triggers = Array.isArray(effectiveTrigger) ? effectiveTrigger : [effectiveTrigger];
    return triggers.includes(trigger);
  });
}

export interface ValidateOptions {
  label: string;
  validateMessages?: ValidateMessages;
}

export async function validateRules(
  value: any,
  rules: FormRule[],
  options: ValidateOptions,
): Promise<string[]> {
  const { label, validateMessages } = options;
  const errors: string[] = [];

  for (const rule of rules) {
    let fieldValue = value;
    if (rule.formatter) {
      fieldValue = rule.formatter(fieldValue, rule);
    }

    const isEmpty = fieldValue === undefined || fieldValue === null || fieldValue === '';
    if (isEmpty && !rule.validateEmpty && !rule.required) {
      continue;
    }

    const msgOptions = { label, validateMessages };

    if (rule.required && isEmpty) {
      errors.push(getMessageFromRule(rule, fieldValue, { type: 'required', ...msgOptions }));
      continue;
    }

    if (rule.pattern && !isEmpty) {
      if (!rule.pattern.test(String(fieldValue))) {
        errors.push(getMessageFromRule(rule, fieldValue, { type: 'pattern', ...msgOptions }));
        continue;
      }
    }

    if (rule.validator) {
      try {
        const result = await rule.validator(fieldValue, rule);
        if (result === false) {
          errors.push(getMessageFromRule(rule, fieldValue, { type: 'validator', ...msgOptions }));
        } else if (typeof result === 'string') {
          errors.push(result);
        }
      } catch {
        errors.push(getMessageFromRule(rule, fieldValue, { type: 'validator', ...msgOptions }));
      }
    }
  }

  return errors;
}
